// Master Dashboard — Feb 22, 2026 (Randy Sprint Requests 4, 5, 7).
//
// pt89c-xix sprint changes on top of pt89f:
//   • ✂️ Killed the swipe-navigation system entirely (Request 4). No more
//     onTouchStart/Move/End, no more left/right chevron nav, no more
//     up/down overlay gestures. Members are entered by TAPPING a name.
//     Overlays open from the pill row (see below). The old kid-vs-parent
//     up-swipe branching + the -1 Capture Pad screen are now stale and
//     have been removed alongside the swipe listeners.
//   • 🍬 Pill row on background tap (Request 5). Four translucent pills
//     — Ask · Capture Pad · Calendar · Leaderboard — fade in on tap
//     and auto-fade after 5s. Ask opens a centered popup with the same
//     component the email-kid / family-kid views use. Capture Pad opens
//     a popup wrapping FamilyCapturePad (same logic as the Staging Capture
//     Pad — inherits Smart Edit upgrades automatically). Calendar +
//     Leaderboard open the existing full-screen overlays. Rewards moved
//     INSIDE each kid's profile screen (see PersonScreen).
//   • 🔒 Kid-lock enforcement with unified 3-minute grace (Request 7).
//     The Master Dashboard is DEFAULT-LOCKED on entry, including for the
//     owner. The ONLY action allowed without a PIN is "Ask Me Anything".
//     Every other interaction (identity/menu, entering a member profile,
//     any of the three deep pills, the rewards store button, and chore
//     approvals) fires the PIN pad on first tap. One successful PIN
//     unlocks ALL guarded actions for 3 minutes (persisted in
//     sessionStorage so a tab close resets it). No double-lock.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CaretLeft, CaretRight, Chat, X, List, DoorOpen, LockKey, Storefront, MagicWand, CalendarBlank, Trophy, House, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CalendarSection from "@/components/CalendarSection";
import FamilyCapturePad from "@/components/FamilyCapturePad";
import FamilyRewards from "@/components/FamilyRewards";
import FamilyAsk from "@/components/FamilyAsk";
import MasterDashboardPinPad from "@/components/MasterDashboardPinPad";
import MasterDashboardAvatar from "@/components/MasterDashboardAvatar";
import MasterDashboardChoreCard from "@/components/MasterDashboardChoreCard";
import { CalendarThemeProvider, useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";
import "./MasterDashboard.css";

// Feb 22, 2026 (Sprint R5) — pill row auto-fade duration.
// Feb 24, 2026 (Randy pt89c-xxi) — bumped from 5s → 10s per Randy.
const PILL_AUTOHIDE_MS = 10_000;
// Feb 22, 2026 (Sprint R7) — one successful PIN unlocks every guarded
// action for this window (kept in sessionStorage so a tab close resets).
const LOCK_GRACE_MS = 3 * 60 * 1000;
const LOCK_STORAGE_KEY = "md_unlocked_at";
// Feb 24, 2026 (Randy pt89c-xxi) — swipe threshold for background-zone
// left/right paging when there are more than 4 members. Chore-card taps
// and checkbox taps still bubble through their own handlers untouched
// because the background listener is only attached to a bare wrapper
// div that sits BEHIND the columns (see `bg-tap-zone` below).
const SWIPE_PX = 60;
const JUMP_BANNER_KEY = (siloId) => `mysilo:md-jump-banner-dismissed:${siloId}`;

const isoDay = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return isoDay(d);
};
const prettyDate = (iso) => new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
  weekday: "long", month: "long", day: "numeric", timeZone: "UTC",
});

// ─────────────────── Lock guard hook (Sprint R7) ───────────────────
// Single unified lock across the whole Master Dashboard. Any successful
// PIN — whether entered for a deep-feature pill, a chore approval, or
// the private-QA hamburger — starts a 3-minute grace where every other
// guarded action passes through without another prompt.
function useLockGuard() {
  const [unlockedAt, setUnlockedAtState] = useState(() => {
    try { return Number(sessionStorage.getItem(LOCK_STORAGE_KEY) || 0) || 0; }
    catch { return 0; }
  });
  const setUnlockedAt = useCallback((ts) => {
    setUnlockedAtState(ts);
    try {
      if (ts) sessionStorage.setItem(LOCK_STORAGE_KEY, String(ts));
      else sessionStorage.removeItem(LOCK_STORAGE_KEY);
    } catch { /* */ }
  }, []);
  const locked = useMemo(() => {
    if (!unlockedAt) return true;
    return (Date.now() - unlockedAt) > LOCK_GRACE_MS;
  }, [unlockedAt]);
  // Re-render every 30s so the lock icon and downstream checks refresh
  // once the grace period elapses.
  useEffect(() => {
    if (!unlockedAt) return undefined;
    const t = setInterval(() => setUnlockedAtState((v) => v), 30_000);
    return () => clearInterval(t);
  }, [unlockedAt]);
  const registerUnlock = useCallback(() => setUnlockedAt(Date.now()), [setUnlockedAt]);
  const lock = useCallback(() => setUnlockedAt(0), [setUnlockedAt]);
  return { locked, registerUnlock, lock };
}

export default function MasterDashboard() {
  const { siloId } = useParams();
  return (
    <CalendarThemeProvider siloId={siloId}>
      <MasterDashboardInner />
    </CalendarThemeProvider>
  );
}

function MasterDashboardInner() {
  const { siloId } = useParams();
  const nav = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [landscape, setLandscape] = useState(true);
  // screen === 0 → Hub (All-Family). screen > 0 → member profile at index (screen - 1).
  // (The old screen === -1 Capture Pad screen was retired with the swipe kill.
  //  Capture Pad is now a popup opened from the Hub pill row instead.)
  const [screen, setScreen] = useState(0);
  // A deep-feature overlay/popup opened from the Hub pill row. One at a time.
  //   "ask" → Ask Me Anything centered popup
  //   "capture" → Capture Pad centered popup
  //   "calendar" → full Calendar overlay
  //   "leaderboard" → full Leaderboard overlay
  //   "storefront" → per-member rewards store (opened from PersonScreen button)
  const [overlay, setOverlay] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  // Pending guarded action to execute right after a successful PIN entry.
  // Shape: { intent: "deep|approve|reject|qa-menu|enter-member|open-pill", payload? }
  const [pinPrompt, setPinPrompt] = useState(null);
  const [exitConfirm, setExitConfirm] = useState(false);
  const [dateISO, setDateISO] = useState(() => isoDay(new Date()));
  // Feb 24, 2026 (Randy pt89c-xxi) — pageStart lifted up here so the outer
  // background-zone swipe listener can paginate members when >4 exist.
  const [pageStart, setPageStart] = useState(0);
  const perPage = 4;
  // Feb 24, 2026 (Randy pt89c-xxi) — jump-banner dismiss lives here so it
  // survives child re-renders. sessionStorage-scoped so it reappears next
  // session (per Randy's Q1 answer this sprint).
  const [jumpBannerDismissed, setJumpBannerDismissed] = useState(() => {
    try { return !!sessionStorage.getItem(JUMP_BANNER_KEY(siloId)); } catch { return false; }
  });
  const dismissJumpBanner = useCallback(() => {
    setJumpBannerDismissed(true);
    try { sessionStorage.setItem(JUMP_BANNER_KEY(siloId), "1"); } catch { /* */ }
  }, [siloId]);
  const { locked, registerUnlock, lock } = useLockGuard();
  // Feb 2026 (Randy pt-md-grace-choreapprove) — the 3-minute grace after
  // a PIN entry is supposed to cover chore approve/reject too, but the
  // approve/reject endpoints on the server still require a PIN in the
  // body. So we cache the last-verified PIN in-memory (never persisted)
  // for the same lifetime as the grace, and inject it into the approve
  // call while the dashboard is unlocked. Result: parents unlock once,
  // approve/reject as many chores as they need for 3 minutes.
  const cachedPinRef = useRef({ pin: null, at: 0 });
  const cachedPin = useCallback(() => {
    const { pin, at } = cachedPinRef.current;
    if (!pin || !at) return null;
    if ((Date.now() - at) > LOCK_GRACE_MS) return null;
    return pin;
  }, []);
  // Wipe the cached PIN whenever the dashboard re-locks (grace expired or
  // owner tapped the Lock button). Same-effect on the `locked` transition.
  useEffect(() => {
    if (locked) cachedPinRef.current = { pin: null, at: 0 };
  }, [locked]);
  // pt89U Stage-C — Re-Themer labels for the wall-tablet's own chrome.
  const tMDTitle       = useThemeLabel(T.THEME_KEY_MD_TITLE);
  const tMDNoPin       = useThemeLabel(T.THEME_KEY_MD_NO_PIN_BANNER);
  const tMDLeaderboard = useThemeLabel(T.THEME_KEY_MD_LEADERBOARD);
  const tMDStore       = useThemeLabel(T.THEME_KEY_MD_STOREFRONT_SUFFIX);
  const tMDCal         = useThemeLabel(T.THEME_KEY_MD_CALENDAR_SUFFIX);
  const tMDExitTitle   = useThemeLabel(T.THEME_KEY_MD_EXIT_TITLE);
  const tMDExitBody    = useThemeLabel(T.THEME_KEY_MD_EXIT_BODY);
  const tMDAllFamily   = useThemeLabel(T.THEME_KEY_MD_ALL_FAMILY);

  // ─── Pill row (Sprint R5) — Hub-only; auto-fades after 10s ───────────
  // Feb 24, 2026 (Randy pt89c-xxi) — desktop hover keeps them visible; any
  // background tap (not on a chore card / avatar / interactive) reveals.
  const [pillsVisible, setPillsVisible] = useState(false);
  const pillTimerRef = useRef(null);
  const clearPillTimer = useCallback(() => {
    if (pillTimerRef.current) { clearTimeout(pillTimerRef.current); pillTimerRef.current = null; }
  }, []);
  const showPills = useCallback(() => {
    if (screen !== 0) return; // Hub only — profile screens don't get the row
    setPillsVisible(true);
    clearPillTimer();
    pillTimerRef.current = setTimeout(() => setPillsVisible(false), PILL_AUTOHIDE_MS);
  }, [screen, clearPillTimer]);
  const holdPills = useCallback(() => {
    // Desktop hover: keep them visible while the pointer sits on the row.
    if (!pillsVisible) return;
    clearPillTimer();
  }, [pillsVisible, clearPillTimer]);
  const releasePills = useCallback(() => {
    // Pointer left the row → restart the 10s countdown.
    if (!pillsVisible) return;
    clearPillTimer();
    pillTimerRef.current = setTimeout(() => setPillsVisible(false), PILL_AUTOHIDE_MS);
  }, [pillsVisible, clearPillTimer]);
  useEffect(() => () => clearPillTimer(), [clearPillTimer]);

  const load = useCallback(async (whichDate) => {
    try {
      const d = whichDate || dateISO;
      const r = await api.get(`/calendar/${siloId}/dashboard`, { params: { date: d } });
      setPayload(r.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Only the owner and parents can open the Master Dashboard.");
        nav(`/dashboard/silo/${siloId}`, { replace: true });
        return;
      }
      toast.error(e.response?.data?.detail || "Couldn't load the dashboard.");
    } finally { setLoading(false); }
  }, [siloId, nav, dateISO]);
  useEffect(() => { load(dateISO); }, [load, dateISO]);
  useEffect(() => {
    // Periodic refresh only on today (past/future days don't change).
    const t = setInterval(() => { if (dateISO === isoDay(new Date())) load(dateISO); }, 30_000);
    return () => clearInterval(t);
  }, [load, dateISO]);

  useEffect(() => {
    const check = () => {
      const isPhone = window.matchMedia("(max-width: 900px)").matches;
      if (!isPhone) { setLandscape(true); return; }
      setLandscape(window.matchMedia("(orientation: landscape)").matches);
    };
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  const members = payload?.members || [];
  const totalScreens = members.length;
  const activePerson = screen > 0 && screen <= totalScreens ? members[screen - 1] : null;

  // ─── Unified lock gate (Sprint R7) ──────────────────────────────────
  // Execute a resolved intent (either directly on unlocked state, or on
  // successful PIN entry). Declared FIRST because runGuarded closes over it.
  const executeIntent = useCallback((intent) => {
    if (!intent) return;
    if (intent.type === "open-menu") setMenuOpen(true);
    else if (intent.type === "open-pill") setOverlay(intent.pill);
    else if (intent.type === "enter-member") setScreen(intent.screenIndex);
    else if (intent.type === "open-storefront") setOverlay("storefront");
    else if (intent.type === "open-qa-menu") setMenuOpen(true);
  }, []);

  // Runs a guarded action IF the dashboard is currently unlocked (within
  // the 3-minute grace). Otherwise stashes the intent and opens the PIN
  // pad; on successful PIN, `approveViaPin` re-invokes the intent.
  const runGuarded = useCallback((intent) => {
    // The Ask pill NEVER runs through here — it's the one exempt action.
    if (!locked) { executeIntent(intent); return; }
    setPinPrompt({ kind: "unlock", intent });
  }, [locked, executeIntent]);

  const toggleChore = async (m, chore) => {
    if (!chore.done && !chore.pending && !payload?.any_pin_set) {
      toast.error("Set a parent PIN first — Household Setup → Randy's row → lock icon.");
      return;
    }
    try {
      const r = await api.post(`/calendar/events/${chore.event_id}/propose-done`, {
        date: chore.date, done: !chore.done && !chore.pending,
      });
      if (r.data?.pending) toast.success(`Waiting on a parent to approve ${chore.title}.`);
      else if (chore.done || chore.pending) toast.success("Cleared.");
      else toast.success("Done ✓");
      load(dateISO);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update.");
    }
  };

  const approveViaPin = async (pin) => {
    if (!pinPrompt) return;
    // Unified unlock lane — any successful PIN entry, regardless of what
    // triggered the prompt, refreshes the 3-minute grace. That's the
    // whole point of the sprint R7 simplification: one code, one grace.
    try {
      if (pinPrompt.kind === "unlock" || pinPrompt.kind === "qa-menu") {
        await api.post(`/calendar/${siloId}/pin/verify`, { pin });
        registerUnlock();
        // Feb 2026 (Randy pt-md-grace-choreapprove) — server verified this
        // PIN, so cache it for the grace lifetime so subsequent chore
        // approvals can pass through without another pad.
        cachedPinRef.current = { pin, at: Date.now() };
        const stashed = pinPrompt.intent;
        setPinPrompt(null);
        if (pinPrompt.kind === "qa-menu") setMenuOpen(true);
        else executeIntent(stashed);
        toast.success("Unlocked (3-minute grace).");
        return;
      }
      const url = pinPrompt.action === "approve"
        ? `/calendar/events/${pinPrompt.eventId}/approve-done`
        : `/calendar/events/${pinPrompt.eventId}/reject-done`;
      await api.post(url, { date: pinPrompt.date, pin });
      // Chore approve/reject uses the same PIN → count it toward the grace.
      registerUnlock();
      cachedPinRef.current = { pin, at: Date.now() };
      toast.success(pinPrompt.action === "approve" ? "Chore confirmed." : "Marked incomplete.");
      setPinPrompt(null);
      load(dateISO);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wrong PIN.");
    }
  };

  // Feb 2026 (Randy pt-md-grace-choreapprove) — during the 3-minute grace
  // window a chore approve/reject skips the PIN pad entirely by reusing
  // the cached PIN. On any 4xx (rejected PIN, kid deleted, etc) we fall
  // back to the pad so nothing gets silently swallowed.
  const approveChoreOrPrompt = useCallback(async (action, chore) => {
    const pin = cachedPin();
    if (locked || !pin) {
      setPinPrompt({ kind: "approve-reject", action, eventId: chore.event_id, date: chore.date });
      return;
    }
    try {
      const url = action === "approve"
        ? `/calendar/events/${chore.event_id}/approve-done`
        : `/calendar/events/${chore.event_id}/reject-done`;
      await api.post(url, { date: chore.date, pin });
      registerUnlock();
      toast.success(action === "approve" ? "Chore confirmed." : "Marked incomplete.");
      load(dateISO);
    } catch (e) {
      // Server rejected the cached PIN (e.g. it was rotated). Drop the
      // cache and re-prompt so the user can enter the current PIN.
      cachedPinRef.current = { pin: null, at: 0 };
      setPinPrompt({ kind: "approve-reject", action, eventId: chore.event_id, date: chore.date });
    }
  }, [locked, cachedPin, siloId, dateISO, load, registerUnlock]);
  const requestExit = () => setExitConfirm(true);

  // Hamburger — always guarded (previously only guarded when Q&A was open).
  const onHamburgerTap = () => {
    if (menuOpen) { setMenuOpen(false); return; }
    runGuarded({ type: "open-menu" });
  };
  const closeQa = () => { setQaOpen(false); };

  // Background tap on Hub → reveal 4-pill row (fades after 10s).
  const onBackgroundTap = useCallback(() => {
    if (screen === 0 && !overlay && !menuOpen && !pinPrompt && !exitConfirm) showPills();
  }, [screen, overlay, menuOpen, pinPrompt, exitConfirm, showPills]);

  // Feb 2026 (Randy pt89c-xxxii) — self-heal an orphaned person-scoped
  // overlay. If a storefront/receipts overlay is somehow still flagged open
  // but we've left the profile (no activePerson), drop it so the Hub never
  // gets stuck thinking a popup is up (which kills the pill row).
  useEffect(() => {
    if ((overlay === "storefront" || overlay === "receipts") && !activePerson) setOverlay(null);
  }, [overlay, activePerson]);

  // Feb 24, 2026 (Randy pt89c-xxi) — horizontal swipe on the Hub paginates
  // ±4 members when the household has more than the visible page. Only
  // active on Hub, no overlay, no PIN pad, no menu, no exit sheet. The
  // touch listeners live at the outermost dashboard div; chore cards +
  // avatars + buttons still get their taps first because we only ACT on
  // the delta at touchend (a tap has ~0 delta and doesn't page).
  const touchStartRef = useRef(null);
  const membersCount = payload?.members?.length || 0;
  const onTouchStartMD = useCallback((e) => {
    if (screen !== 0 || overlay || menuOpen || pinPrompt || exitConfirm) return;
    const t = e.touches?.[0]; if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }, [screen, overlay, menuOpen, pinPrompt, exitConfirm]);
  const onTouchEndMD = useCallback((e) => {
    const s = touchStartRef.current; touchStartRef.current = null;
    if (!s) return;
    const t = e.changedTouches?.[0]; if (!t) return;
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    // Ignore vertical scrolls + slow long-holds — only real horizontal swipes.
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (Date.now() - s.t > 800) return;
    if (dx < 0 && pageStart + perPage < membersCount) setPageStart((p) => p + perPage);
    else if (dx > 0 && pageStart > 0) setPageStart((p) => Math.max(0, p - perPage));
  }, [pageStart, membersCount]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0B0D10] text-[#EAECEE] flex items-center justify-center text-sm" data-testid="master-dashboard-loading">
        Loading the family dashboard…
      </div>
    );
  }
  if (!landscape) {
    return (
      <div className="fixed inset-0 bg-[#0B0D10] text-[#EAECEE] flex items-center justify-center p-8 text-center" data-testid="master-dashboard-rotate">
        <div className="max-w-sm">
          <div className="text-4xl mb-4">↻</div>
          <div className="font-serif-display text-2xl mb-2">Rotate your device</div>
          <div className="text-sm text-[#8A8F94]">The Master Dashboard is a horizontal-only experience on phones. Rotate to landscape.</div>
        </div>
      </div>
    );
  }
  if (!payload) return null;

  const slug = payload.silo?.slug;
  const showingToday = dateISO === isoDay(new Date());

  // Pill row activator — a runGuarded wrapper that closes the pill row too.
  const onPillTap = (pill) => {
    setPillsVisible(false);
    if (pillTimerRef.current) { clearTimeout(pillTimerRef.current); pillTimerRef.current = null; }
    if (pill === "ask" || pill === "leaderboard") {
      // Ask Me Anything + Leaderboard are view-only — no PIN required
      // (Randy pt89c-xxix: unlock the leaderboard pill).
      setOverlay(pill);
      return;
    }
    runGuarded({ type: "open-pill", pill });
  };

  // Tapping a member card enters their profile screen. Feb 25, 2026
  // (Randy pt89c-xxix): viewing a member's profile no longer needs a PIN —
  // the deeper actions inside (rewards store, chore approvals) stay gated.
  const onMemberTap = (screenIndex) => {
    setScreen(screenIndex);
  };
  // Feb 2026 (Randy pt89c-xxxii) — one true "go home to the All Family hub"
  // action: clears BOTH the profile screen AND any open overlay. The old
  // header chip only did setScreen(0), which orphaned a storefront/receipts
  // overlay (activePerson went null so the overlay hid, but `overlay` stayed
  // truthy and permanently suppressed the Hub pill row). Root-cause fix for
  // "pills break after visiting a profile's rewards/ticket area".
  const goHub = () => { setOverlay(null); setScreen(0); };

  return (
    <div
      className="fixed inset-0 overflow-hidden bg-[#0B0D10] text-[#EAECEE] select-none"
      onClick={onBackgroundTap}
      onTouchStart={onTouchStartMD}
      onTouchEnd={onTouchEndMD}
      data-testid="master-dashboard"
    >
      <header className="h-12 flex items-center gap-4 px-6 border-b border-white/5 bg-black/30 backdrop-blur sticky top-0 z-[260]">
        <div className="font-serif-display text-base tracking-wide" data-testid="master-dashboard-title">{payload.silo?.name || "Family"}</div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-white/40">{tMDTitle}</div>
        {/* Persistent lock indicator — reflects the unified 3-min grace. */}
        <span
          data-testid="master-dashboard-lock-status"
          title={locked ? "Locked — a PIN is required to open guarded features" : "Unlocked for 3 minutes"}
          className={`text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border flex items-center gap-1 ${
            locked ? "bg-[#E8C170]/10 border-[#E8C170]/40 text-[#E8C170]"
                    : "bg-[#4A9E6A]/10 border-[#4A9E6A]/40 text-[#7BD094]"
          }`}
        >
          <LockKey size={9} weight="duotone" /> {locked ? "Locked" : "Unlocked"}
        </span>
        {/* Back-to-Hub chip on member profile screens (Sprint R4) — replaces
            the old L/R swipe/chevron nav. Tap the silo name at top-left OR
            this chip to return. */}
        {(screen !== 0 || overlay) && (
          <button onClick={(e) => { e.stopPropagation(); goHub(); }}
            data-testid="master-dashboard-back-hub"
            className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border border-white/15 text-white/70 hover:text-white hover:border-white/40 flex items-center gap-1">
            <House size={11} weight="duotone" /> {tMDAllFamily}
          </button>
        )}
        <div className="flex-1" />
        {/* Date + tiny nav arrows */}
        <div className="flex items-center gap-1 text-xs text-white/60 tabular-nums">
          <button onClick={(e) => { e.stopPropagation(); setDateISO((d) => addDays(d, -1)); }} aria-label="Previous day"
            data-testid="master-dashboard-date-prev"
            className="w-4 h-4 flex items-center justify-center text-white/25 hover:text-white/70">
            <CaretLeft size={9} weight="light" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setDateISO(isoDay(new Date())); }} title="Jump to today"
            data-testid="master-dashboard-date-label"
            className={`px-1 hover:text-white/90 ${!showingToday ? "text-[#E8C170]" : ""}`}>
            {prettyDate(dateISO)}
          </button>
          <button onClick={(e) => { e.stopPropagation(); setDateISO((d) => addDays(d, 1)); }} aria-label="Next day"
            data-testid="master-dashboard-date-next"
            className="w-4 h-4 flex items-center justify-center text-white/25 hover:text-white/70">
            <CaretRight size={9} weight="light" />
          </button>
        </div>
        {/* Hamburger menu — ALWAYS PIN-gated (Sprint R7). */}
        <div className="relative">
          <button onClick={(e) => { e.stopPropagation(); onHamburgerTap(); }}
            data-testid="master-dashboard-menu"
            className={`p-1.5 rounded border text-white/70 hover:text-white ${locked ? "border-[#E8C170]/60 hover:border-[#E8C170]" : "border-white/10 hover:border-white/30"}`}
            title={locked ? "Locked — enter a parent PIN to unlock" : "Menu"}>
            {locked ? <LockKey size={16} weight="duotone" /> : <List size={16} weight="bold" />}
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-[265]" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-56 rounded-lg bg-[#12151A] border border-white/10 shadow-2xl z-[270]" data-testid="master-dashboard-menu-dropdown">
                <button onClick={() => { setMenuOpen(false); setQaOpen(true); }}
                  data-testid="master-dashboard-menu-qa"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/85 hover:bg-white/5 border-b border-white/5">
                  <Chat size={14} weight="duotone" /> Public Q&amp;A
                </button>
                <button onClick={() => { setMenuOpen(false); lock(); toast.success("Locked."); }}
                  data-testid="master-dashboard-menu-lock-now"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/85 hover:bg-white/5 border-b border-white/5">
                  <LockKey size={14} weight="duotone" /> Lock now
                </button>
                <button onClick={() => { setMenuOpen(false); requestExit(); }}
                  data-testid="master-dashboard-menu-exit"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/85 hover:bg-white/5">
                  <DoorOpen size={14} weight="duotone" /> Exit dashboard
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* "Set a parent PIN first" banner when no PIN exists yet. */}
      {!payload.any_pin_set && !overlay && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-[#E8C170]/10 border border-[#E8C170]/30 text-[10px] uppercase tracking-[0.2em] text-[#E8C170] flex items-center gap-2"
          data-testid="master-dashboard-no-pin-banner">
          <LockKey size={12} weight="duotone" /> {tMDNoPin}
        </div>
      )}

      {/* Screen viewport — no swipe transform, no L/R chevron nav (Sprint R4) */}
      <div className="relative h-[calc(100dvh-3rem)]">
        {screen === 0 && (
          <AllFamilyScreen payload={payload}
            onChoreTap={toggleChore}
            onPending={(chore) => approveChoreOrPrompt("approve", chore)}
            onPendingReject={(chore) => approveChoreOrPrompt("reject", chore)}
            onJumpToNext={(iso) => setDateISO(iso)}
            onMemberTap={onMemberTap}
            pageStart={pageStart}
            setPageStart={setPageStart}
            perPage={perPage}
            jumpBannerDismissed={jumpBannerDismissed}
            onDismissJumpBanner={dismissJumpBanner}
            addExtraTopPad={pillsVisible} />
        )}
        {activePerson && (
          <PersonScreen key={activePerson.target_id}
            siloId={siloId} member={activePerson}
            balance={payload.balances?.[activePerson.target_id]}
            chores={payload.chores_by_member?.[activePerson.target_id] || []}
            onChoreTap={toggleChore}
            onPending={(chore) => approveChoreOrPrompt("approve", chore)}
            onPendingReject={(chore) => approveChoreOrPrompt("reject", chore)}
            onOpenRewards={() => setOverlay("storefront")}
            onViewReceipts={() => setOverlay("receipts")}
            onAvatarUpdated={() => load(dateISO)} />
        )}
      </div>

      {/* ─── Hub-only pill row (Sprint R5) ─── */}
      {/* Feb 24, 2026 (Randy pt89c-xxi) — relocated from bottom to just below
          the header, ABOVE the member names row. Fixes the mobile 2-row
          squeeze and matches Randy's placement preference. Desktop hover
          keeps the row visible (no fade while pointer is over it). */}
      {screen === 0 && pillsVisible && !overlay && (
        <div
          data-testid="master-dashboard-pill-row"
          className="absolute left-1/2 -translate-x-1/2 top-14 z-20 flex items-center gap-1.5 animate-fade-in flex-nowrap justify-center max-w-[97vw]"
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={holdPills}
          onMouseLeave={releasePills}
        >
          <PillButton testid="master-dashboard-pill-ask" icon={<Chat size={11} weight="duotone" />} label="Ask" onClick={() => onPillTap("ask")} />
          <PillButton testid="master-dashboard-pill-capture" icon={<MagicWand size={11} weight="duotone" />} label="Capture" onClick={() => onPillTap("capture")} locked={locked} />
          <PillButton testid="master-dashboard-pill-calendar" icon={<CalendarBlank size={11} weight="duotone" />} label="Calendar" onClick={() => onPillTap("calendar")} locked={locked} />
          <PillButton testid="master-dashboard-pill-leaderboard" icon={<Trophy size={11} weight="duotone" />} label="Leaderboard" onClick={() => onPillTap("leaderboard")} />
        </div>
      )}

      {/* ─── Overlays & popups ─── */}
      {overlay === "ask" && slug && (
        <CenteredPopup title="Ask me anything" testid="master-dashboard-ask-popup" onClose={() => setOverlay(null)}>
          <FamilyAsk siloSlug={slug} siloId={siloId} defaultOpen={true} />
        </CenteredPopup>
      )}
      {overlay === "capture" && (
        <CenteredPopup title="Capture pad" testid="master-dashboard-capture-popup" onClose={() => setOverlay(null)}>
          <FamilyCapturePad siloId={siloId} siloName={payload.silo?.name} siloSlug={slug} inheritedSkus={[]} />
        </CenteredPopup>
      )}
      {overlay === "calendar" && (
        <OverlayPane title={tMDCal.replace(/^[a-z]/, (c) => c.toUpperCase())} onClose={() => setOverlay(null)}>
          <div className="master-dashboard-dense">
            <CalendarSection siloId={siloId} siloName={payload.silo?.name} siloSlug={slug} />
          </div>
        </OverlayPane>
      )}
      {overlay === "leaderboard" && (
        <OverlayPane title={tMDLeaderboard} onClose={() => setOverlay(null)}>
          <FamilyRewards siloId={siloId} panel="leaderboard" />
        </OverlayPane>
      )}
      {overlay === "storefront" && activePerson && (
        <OverlayPane title={`${activePerson.first_name} · ${tMDStore}`} onClose={() => setOverlay(null)} onHome={goHub}>
          <MemberCheckout siloId={siloId} member={activePerson} />
        </OverlayPane>
      )}
      {overlay === "receipts" && activePerson && (
        <OverlayPane title={`${activePerson.first_name} · Receipts`} onClose={() => setOverlay(null)} onHome={goHub}>
          <MemberReceipts siloId={siloId} member={activePerson} />
        </OverlayPane>
      )}

      {pinPrompt && (
        <MasterDashboardPinPad
          onCancel={() => setPinPrompt(null)}
          onSubmit={approveViaPin}
          heading={
            pinPrompt.kind === "unlock" ? "Enter parent PIN"
            : pinPrompt.kind === "qa-menu" ? "Unlock menu"
            : pinPrompt.action === "approve" ? "Approve chore"
            : "Reject chore"
          }
        />
      )}

      {qaOpen && slug && <QaShelf slug={slug} onClose={closeQa} />}

      {exitConfirm && (
        <div className="fixed inset-0 z-[300] bg-black/70 flex items-center justify-center p-6" onClick={() => setExitConfirm(false)}>
          <div className="modal-safe bg-[#12151A] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="master-dashboard-exit-confirm">
            <div className="font-serif-display text-xl mb-2">{tMDExitTitle}</div>
            <div className="text-sm text-white/60 mb-5">{tMDExitBody}</div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setExitConfirm(false)} className="text-xs uppercase tracking-[0.18em] px-3 py-2 text-white/60">Stay</button>
              <button onClick={() => nav(`/dashboard/silo/${siloId}`)} data-testid="master-dashboard-exit-confirm-btn"
                className="text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-md bg-white text-black hover:bg-white/90">Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────── Pill button ───────────────
function PillButton({ testid, icon, label, onClick, locked }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 rounded-full bg-white/8 backdrop-blur border border-white/15 text-[9px] uppercase tracking-[0.08em] whitespace-nowrap text-white/85 hover:bg-white/16 hover:border-white/30 transition flex items-center gap-1 shrink-0"
    >
      {icon}
      {label}
      {locked && <LockKey size={9} weight="duotone" className="text-[#E8C170] opacity-70" />}
    </button>
  );
}

// ─────────────── All-Family (anchor / Hub) ───────────────
function AllFamilyScreen({ payload, onChoreTap, onPending, onPendingReject, onJumpToNext, onMemberTap, pageStart, setPageStart, perPage, jumpBannerDismissed, onDismissJumpBanner, addExtraTopPad }) {
  const members = payload?.members || [];
  const visible = members.slice(pageStart, pageStart + perPage);
  const canPrev = pageStart > 0;
  const canNext = pageStart + perPage < members.length;
  const totalToday = members.reduce((n, m) => n + (payload?.chores_by_member?.[m.target_id]?.length || 0), 0);
  const nextDate = payload?.next_chore_date;
  const nextCount = payload?.next_chore_count || 0;
  // Feb 24, 2026 (Randy pt89c-xxi) — banner is dismissable per session.
  const showJumpBanner = totalToday === 0 && !!nextDate && !jumpBannerDismissed;
  const prettyNextDate = showJumpBanner
    ? new Date(nextDate + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })
    : "";
  return (
    <div className={`h-full flex flex-col p-8 ${addExtraTopPad ? "pt-20" : "pt-4"}`} data-testid="master-dashboard-all-family">
      {showJumpBanner && (
        <div className="mb-4 mx-2 rounded-lg border border-white/15 bg-white/[0.04] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="master-dashboard-jump-banner"
          onClick={(e) => e.stopPropagation()}>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">No chores today</div>
            <div className="text-sm text-white/85 mt-1">
              Your next scheduled day is <span className="font-medium text-white">{prettyNextDate}</span>
              {nextCount > 0 && <span className="text-white/60"> · {nextCount} chore{nextCount === 1 ? "" : "s"} that day</span>}
            </div>
          </div>
          <button
            onClick={() => onJumpToNext && onJumpToNext(nextDate)}
            data-testid="master-dashboard-jump-btn"
            className="text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/25 text-white/90 hover:bg-white/10 transition"
          >
            Jump to {prettyNextDate}
          </button>
          <button
            onClick={onDismissJumpBanner}
            data-testid="master-dashboard-jump-dismiss"
            aria-label="Dismiss until next session"
            title="Dismiss until next session"
            className="p-1 rounded text-white/40 hover:text-white/90 hover:bg-white/10"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}
      <div className="flex-1 grid grid-cols-4 gap-6 min-h-0">
        {visible.map((m, i) => (
          <MemberColumn key={m.target_id} member={m} siloId={payload.silo?.id}
            chores={payload.chores_by_member?.[m.target_id] || []}
            onChoreTap={onChoreTap} onPending={onPending} onPendingReject={onPendingReject}
            onMemberTap={() => onMemberTap(pageStart + i + 1)} />
        ))}
        {Array.from({ length: Math.max(0, perPage - visible.length) }).map((_, i) => (
          <div key={`ph-${i}`} className="opacity-30 border border-dashed border-white/8 rounded-xl" />
        ))}
      </div>
      {(canPrev || canNext) && (
        <div className="mt-4 flex items-center justify-center gap-6 text-white/50" onClick={(e) => e.stopPropagation()}>
          <button disabled={!canPrev} onClick={() => setPageStart(Math.max(0, pageStart - perPage))}
            data-testid="master-dashboard-column-prev" className="p-2 disabled:opacity-20 hover:text-white">
            <CaretLeft size={18} />
          </button>
          <span className="text-[10px] uppercase tracking-[0.24em]">{pageStart + 1}–{Math.min(members.length, pageStart + perPage)} of {members.length} · swipe ← → to page</span>
          <button disabled={!canNext} onClick={() => setPageStart(Math.min(members.length - perPage, pageStart + perPage))}
            data-testid="master-dashboard-column-next" className="p-2 disabled:opacity-20 hover:text-white">
            <CaretRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

function MemberColumn({ member, siloId, chores, onChoreTap, onPending, onPendingReject, onMemberTap }) {
  return (
    // Feb 24, 2026 (Randy pt89c-xxi) — no outer stopPropagation. Empty
    // space inside the column bubbles up to the dashboard's onClick so a
    // tap anywhere reveals the pill row. Interactive elements below (the
    // name button + individual chore cards) still stopPropagation on
    // their own so their taps don't accidentally trigger the reveal.
    <div className="flex flex-col min-h-0" data-testid={`master-dashboard-column-${member.target_id}`}>
      {/* The name/avatar row is the ENTER-PROFILE affordance (Sprint R4). */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onMemberTap?.(); }}
        data-testid={`master-dashboard-name-${member.target_id}`}
        className="flex items-center gap-2 pb-3 border-b border-white/8 text-left w-full hover:bg-white/[0.03] rounded-t-md px-1 -mx-1 transition"
        title={`Open ${member.first_name}'s profile (PIN required)`}
      >
        <MasterDashboardAvatar siloId={siloId} member={member} size={28} interactive={false} />
        <div className="font-serif-display text-lg tracking-wide truncate flex-1">
          {member.first_name}
        </div>
        <CaretRight size={13} weight="bold" className="text-white/30 group-hover:text-white/70" />
      </button>
      <div className="flex-1 overflow-y-auto pt-3 space-y-2 pr-1">
        {chores.length === 0 && (
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/25 py-6 text-center">Nothing today</div>
        )}
        {chores.map((c) => (
          <div key={c.event_id + c.date} onClick={(e) => e.stopPropagation()}>
            <MasterDashboardChoreCard chore={c}
              onTap={() => onChoreTap(member, c)}
              onApprove={() => onPending(c)}
              onReject={() => onPendingReject(c)}
              compact />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────── Per-person page ───────────────
function PersonScreen({ siloId, member, balance, chores, onChoreTap, onPending, onPendingReject, onAvatarUpdated, onOpenRewards, onViewReceipts }) {
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep. Wires the two unwired
  // pt89U keys (Today's chores / Nothing today) + the two profile pills.
  const tTodayHeading = useThemeLabel(T.THEME_KEY_MD_TODAY_HEADING);
  const tNothingToday = useThemeLabel(T.THEME_KEY_MD_NOTHING_TODAY);
  const tRewardsPill  = useThemeLabel(T.THEME_KEY_MD_REWARDS_STORE_PILL);
  const tReceiptsPill = useThemeLabel(T.THEME_KEY_MD_VIEW_RECEIPTS_PILL);
  return (
    <div className="h-full flex flex-col p-10 pt-14" data-testid={`master-dashboard-person-${member.target_id}`}
      onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-6">
        <MasterDashboardAvatar siloId={siloId} member={member} size={96} interactive onUpdated={onAvatarUpdated} />
        <div className="flex-1">
          <div className="font-serif-display text-5xl tracking-wide" data-testid={`master-dashboard-person-name-${member.target_id}`}>{member.first_name}</div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 mt-2 flex items-center gap-4 flex-wrap">
            <span data-testid={`master-dashboard-person-points-${member.target_id}`} className="text-[#E8C170]">{balance?.spendable ?? 0} to spend</span>
            <span data-testid={`master-dashboard-person-lifetime-${member.target_id}`} className="text-white/50">{balance?.lifetime_points ?? 0} lifetime</span>
            {(balance?.best_streak ?? 0) > 0 && (
              <span data-testid={`master-dashboard-person-streak-${member.target_id}`}>🔥 {balance.best_streak}-day streak</span>
            )}
          </div>
        </div>
        {/* Sprint R5 — Rewards Store lives on the kid's own profile screen
            (moved out of the Hub pill row so it knows which kid). Feb 2026
            (Randy) — UNLOCKED: viewing the rewards store + receipts inside a
            member's profile no longer requires a PIN. */}
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenRewards?.(); }}
            data-testid="member-screen-rewards-pill"
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-white/8 backdrop-blur border border-white/15 text-white/85 hover:bg-white/16 hover:border-white/30 flex items-center gap-1.5"
          >
            <Storefront size={11} weight="duotone" /> {tRewardsPill}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onViewReceipts?.(); }}
            data-testid="member-screen-receipts-pill"
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-white/8 backdrop-blur border border-white/15 text-white/85 hover:bg-white/16 hover:border-white/30 flex items-center gap-1.5"
          >
            <Receipt size={11} weight="duotone" /> {tReceiptsPill}
          </button>
        </div>
      </div>
      <div className="mt-8 flex-1 overflow-y-auto min-h-0">
        <div className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-3">{tTodayHeading}</div>
        {chores.length === 0 && (
          <div className="text-sm text-white/40 py-8">{tNothingToday}</div>
        )}
        <div className="space-y-3 max-w-2xl">
          {chores.map((c) => (
            <MasterDashboardChoreCard key={c.event_id + c.date} chore={c}
              onTap={() => onChoreTap(member, c)}
              onApprove={() => onPending(c)}
              onReject={() => onPendingReject(c)} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────── Per-member receipts (View Receipts) ───────────────
// Feb 2026 (Randy) — lists every reward this member has checked out
// (redeemed), any status, newest first. Reads the new parent-actor-safe
// /member-redemptions endpoint so it works for the owner AND parent-pill
// members alike.
function MemberReceipts({ siloId, member }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [bal, setBal] = useState({ spendable: 0, lifetime: 0 });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/calendar/${siloId}/member-redemptions/${encodeURIComponent(member.target_id)}`);
        if (!alive) return;
        setRows(res.data?.redemptions || []);
        setBal({ spendable: res.data?.points_spendable ?? 0, lifetime: res.data?.points_lifetime ?? 0 });
      } catch { if (alive) setRows([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [siloId, member.target_id]);

  const STATUS_META = {
    pending:   { label: "Awaiting approval", cls: "text-[#8A6A24] bg-[#FDF9EF] border-[#EAD9AE]" },
    approved:  { label: "Approved · in wallet", cls: "text-[#2F6B3A] bg-[#EAF3EC] border-[#B9D4BE]" },
    delivered: { label: "Delivered", cls: "text-[#4A5D4E] bg-[#EEF1EE] border-[#D7DED8]" },
    declined:  { label: "Declined", cls: "text-[#C16E5A] bg-[#FBEEEA] border-[#EBC7BC]" },
  };
  const prettyTs = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="member-receipts">
      <div className="flex items-center gap-3 mb-4">
        <div className="text-xs px-3 py-1.5 rounded-full bg-[#F8F1E2] border border-[#EAD9AE] text-[#8A6A24] font-medium" data-testid="member-receipts-spendable">
          ⭐ {bal.spendable} to spend
        </div>
        <div className="text-xs px-3 py-1.5 rounded-full bg-[#F0F1EE] border hairline text-[#6B6A68] font-medium" data-testid="member-receipts-lifetime">
          {bal.lifetime} lifetime
        </div>
      </div>
      {loading && <div className="text-sm text-[#6B6A68] py-6" data-testid="member-receipts-loading">Loading receipts…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-[#6B6A68] py-8 text-center" data-testid="member-receipts-empty">
          No rewards checked out yet. Head to the Rewards Store to redeem earned points.
        </div>
      )}
      <div className="space-y-2">
        {rows.map((red) => {
          const meta = STATUS_META[red.status] || { label: red.status, cls: "text-[#6B6A68] bg-white border hairline" };
          return (
            <div key={red.id} data-testid={`member-receipt-${red.id}`}
              className="flex items-center gap-3 bg-white border hairline rounded-lg px-3 py-2.5">
              <span className="text-2xl leading-none">{red.reward_emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1C1B1A] truncate">{red.reward_name}</div>
                <div className="text-[11px] text-[#6B6A68]">{red.cost_points} pts · {prettyTs(red.created_at)}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full border shrink-0 ${meta.cls}`}>
                {meta.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────── Member checkout (Feb 2026, Randy) ───────────────
// The kid's OWN checkout on the wall tablet: when a member is inside their
// own profile screen and opens the Rewards Store, this is where they spend
// the chore points they earned on fictitious rewards. A parent-actor is the
// authenticated user, so checkout routes through `redeem-for/{member_id}` —
// points are validated against THIS member's balance and the claim lands in
// the normal approval inbox (nothing auto-granted). This is the chore-points
// Rewards Store, NOT the Pro Creator storefront.
function MemberCheckout({ siloId, member }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [status, setStatus] = useState(null); // { kind: "ok"|"err", msg }
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calendar/${siloId}/member-redemptions/${encodeURIComponent(member.target_id)}`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [siloId, member.target_id]);
  useEffect(() => { load(); }, [load]);

  const spendable = data?.points_spendable ?? 0;
  const checkout = async (rw) => {
    if (rw.cost_points > spendable) {
      setStatus({ kind: "err", msg: `Not enough points — ${member.first_name} has ${spendable} to spend.` });
      return;
    }
    setBusyId(rw.id); setStatus(null);
    try {
      await api.post(`/calendar/${siloId}/rewards/${encodeURIComponent(rw.id)}/redeem-for/${encodeURIComponent(member.target_id)}`);
      setStatus({ kind: "ok", msg: `Checked out "${rw.name}" — sent to a parent to approve.` });
      await load();
    } catch (e) {
      setStatus({ kind: "err", msg: e.response?.data?.detail || "Couldn't check that out." });
    } finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="text-sm text-[#6B6A68] py-8 text-center" data-testid="member-checkout-loading">Loading rewards…</div>;
  }
  const rewards = data?.rewards || [];
  const pending = (data?.redemptions || []).filter((x) => x.status === "pending");
  const achieved = (data?.redemptions || []).filter((x) => x.status === "approved");

  return (
    <div className="max-w-2xl mx-auto" data-testid="member-checkout">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm px-3.5 py-2 rounded-full bg-[#F8F1E2] border border-[#EAD9AE] text-[#8A6A24] font-medium" data-testid="member-checkout-spendable">
          ⭐ {spendable} to spend
        </span>
        <span className="text-xs px-3 py-2 rounded-full bg-[#EFEFEC] border border-[#DCDBD6] text-[#6B6A68] font-medium" data-testid="member-checkout-lifetime">
          {data?.points_lifetime ?? 0} lifetime
        </span>
      </div>

      {status && (
        <div data-testid="member-checkout-status"
          className={`mb-4 text-sm rounded-lg px-3.5 py-2.5 border ${
            status.kind === "ok" ? "bg-[#EAF3EC] text-[#2F6B3A] border-[#B9D4BE]"
                                  : "bg-[#F7E6E1] text-[#C16E5A] border-[#E7C6BC]"}`}>
          {status.msg}
        </div>
      )}

      <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Rewards Store</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rewards.map((rw) => {
          const afford = rw.cost_points <= spendable;
          return (
            <button key={rw.id} onClick={() => checkout(rw)} disabled={!afford || busyId === rw.id}
              data-testid={`member-checkout-reward-${rw.id}`}
              className={`flex items-center gap-2 text-left p-3 rounded-lg border-2 transition-colors ${
                afford ? "border-[#DCE5DD] hover:border-[#4A5D4E] bg-white"
                       : "border-[#EDECE8] bg-[#FAFAF8] opacity-60 cursor-not-allowed"}`}>
              <span className="text-2xl leading-none">{rw.emoji}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-[#1C1B1A] truncate">{rw.name}</span>
                <span className="text-[11px] text-[#8A6A24] font-medium">{rw.cost_points} pts</span>
              </span>
              {busyId === rw.id && <span className="text-[11px] text-[#6B6A68]">…</span>}
            </button>
          );
        })}
        {rewards.length === 0 && (
          <div className="text-xs text-[#6B6A68] italic col-span-full py-2">No rewards set up yet — a parent can add some from the AI Calendar.</div>
        )}
      </div>

      {pending.length > 0 && (
        <div className="mt-4 bg-[#FDF9EF] border border-[#EAD9AE] rounded-md p-3" data-testid="member-checkout-pending">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A6A24] mb-1">⏳ Waiting for a parent's OK</div>
          {pending.map((x) => (
            <div key={x.id} className="text-sm text-[#1C1B1A]">• {x.reward_emoji} {x.reward_name} ({x.cost_points} pts)</div>
          ))}
        </div>
      )}

      {achieved.length > 0 && (
        <div className="mt-3" data-testid="member-checkout-achieved">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#2F6B3A] mb-1">✓ Approved — ready to use</div>
          {achieved.map((x) => (
            <div key={x.id} className="text-sm text-[#1C1B1A]">• {x.reward_emoji} {x.reward_name} ({x.cost_points} pts)</div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverlayPane({ title, onClose, onHome, children }) {
  const tAllFamily = useThemeLabel(T.THEME_KEY_MD_ALL_FAMILY);
  return (
    // Feb 2026 (Randy pt89c-xxxii) — two guaranteed exits pinned ABOVE the MD
    // header and any inner component header: an "All Family" chip (top-left)
    // that goes all the way home to the hub, and an X (top-right) that closes
    // one level. Both use max()+safe-area so they clear the iOS notch, and
    // the content padding respects the notch so the leaderboard points aren't
    // hidden under the Dynamic Island in landscape.
    <div className="absolute inset-0 z-[150] bg-[#F7F5EF] text-[#1C1B1A] flex flex-col" data-testid="master-dashboard-overlay"
      onClick={(e) => e.stopPropagation()}>
      <button
        onClick={onHome || onClose}
        data-testid="master-dashboard-overlay-back-hub"
        title="Back to All Family"
        className="fixed top-3 z-[400] text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-full bg-black/85 text-white border border-white/20 hover:bg-black flex items-center gap-1.5 shadow-lg"
        style={{ left: "max(0.75rem, env(safe-area-inset-left))" }}
      >
        <House size={11} weight="duotone" /> {tAllFamily}
      </button>
      <button
        onClick={onClose}
        data-testid="master-dashboard-overlay-close"
        title="Close" aria-label="Close"
        className="fixed top-3 z-[400] w-8 h-8 rounded-full bg-black/85 text-white border border-white/20 hover:bg-black flex items-center justify-center shadow-lg"
        style={{ right: "max(0.75rem, env(safe-area-inset-right))" }}
      >
        <X size={16} weight="bold" />
      </button>
      <div className="h-11 flex items-center gap-3 pl-32 pr-16 border-b hairline bg-white shrink-0">
        <div className="font-serif-display text-base truncate">{title}</div>
      </div>
      <div className="flex-1 overflow-y-auto py-5 min-h-0"
        style={{ paddingLeft: "max(1.25rem, env(safe-area-inset-left))", paddingRight: "max(1.25rem, env(safe-area-inset-right))" }}>
        {children}
      </div>
    </div>
  );
}

// Sprint R5 — small centered popup wrapper used by the Ask + Capture Pad
// pills. Same footprint on Hub so both feel like siblings.
function CenteredPopup({ title, testid, onClose, children }) {
  const tAllFamily = useThemeLabel(T.THEME_KEY_MD_ALL_FAMILY);
  return (
    <div className="fixed inset-0 z-[280] bg-black/70 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}>
      <div className="bg-[#F7F5EF] text-[#1C1B1A] rounded-2xl w-full max-w-3xl max-h-[86vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="h-11 flex items-center gap-3 px-5 border-b hairline shrink-0">
          <button onClick={onClose}
            data-testid={`${testid}-back-hub`}
            title="Back to All Family"
            className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-black/85 text-white border border-white/15 hover:bg-black flex items-center gap-1.5">
            <House size={11} weight="duotone" /> {tAllFamily}
          </button>
          <div className="font-serif-display text-base truncate">{title}</div>
          <div className="flex-1" />
          <button onClick={onClose}
            data-testid={`${testid}-close`}
            className="p-1.5 rounded text-[#6B6A68] hover:bg-[#F0F1EE]" title="Close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
}

function QaShelf({ slug, onClose }) {
  // Feb 22, 2026 (Sprint R7) — the old `locked` prop is retired here. The
  // main dashboard's unified lock already gates access to the hamburger
  // that opens this shelf; no need for a second inline lock chip.
  const SCALE = 0.55;
  const LOGICAL_WIDTH = 1000;
  return (
    <div className="fixed left-0 right-0 bottom-0 top-12 z-[250] flex" data-testid="master-dashboard-qa-shelf">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full sm:w-[620px] max-w-full bg-[#12151A] border-l border-white/10 flex flex-col shadow-2xl"
           style={{ paddingRight: "env(safe-area-inset-right)" }}>
        <div className="h-11 flex items-center gap-3 px-4 border-b border-white/10 shrink-0">
          <Chat size={13} weight="duotone" className="text-white/60" />
          <div className="font-serif-display text-base text-white">Silo Q&amp;A</div>
          <div className="flex-1" />
          <button onClick={onClose} data-testid="master-dashboard-qa-close" className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/5">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 relative bg-white overflow-hidden">
          <iframe key={slug} title="Silo Q&A" src={`${window.location.origin}/s/${slug}?embedded=1`}
            data-testid="master-dashboard-qa-iframe"
            style={{
              width: `${LOGICAL_WIDTH}px`,
              height: `calc(100% / ${SCALE})`,
              transform: `scale(${SCALE})`,
              transformOrigin: "top left",
              border: 0,
              display: "block",
            }} />
        </div>
      </div>
    </div>
  );
}
